import { z } from "zod";

import { CalcDecimal, decimalString } from "./decimal.js";
import type { ResolvedCalculationInput } from "./schema.js";

const identifier = z.string().min(1).max(100).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const positiveDecimal = z
  .string()
  .regex(/^(0|[1-9]\d*)(\.\d+)?$/)
  .refine((value) => new CalcDecimal(value).greaterThan(0));

export const categoryGroupSchema = z.enum([
  "EDILIZIA",
  "STRUTTURE",
  "IMPIANTI",
  "VIABILITA",
  "IDRAULICA",
  "TIC",
  "AGRICOLTURA_AMBIENTE",
  "TERRITORIO_URBANISTICA",
]);

const qByGroupSchema = z
  .object({
    EDILIZIA: positiveDecimal.nullable(),
    STRUTTURE: positiveDecimal.nullable(),
    IMPIANTI: positiveDecimal.nullable(),
    VIABILITA: positiveDecimal.nullable(),
    IDRAULICA: positiveDecimal.nullable(),
    TIC: positiveDecimal.nullable(),
    AGRICOLTURA_AMBIENTE: positiveDecimal.nullable(),
    TERRITORIO_URBANISTICA: positiveDecimal.nullable(),
  })
  .strict();

export const referenceRulesetSchema = z
  .object({
    schema_version: z.literal("1.0.0"),
    ruleset_id: identifier,
    version: identifier,
    status: z.literal("draft"),
    scope: z.literal("official_example_reference_only"),
    formula_id: z.literal("dm140-2012-cp-v1"),
    source: z
      .object({
        title: z.string().min(1),
        publication: z.string().min(1),
        official_url: z.string().url(),
        archive_path: z.string().min(1),
        sha256,
      })
      .strict(),
    approval: z
      .object({
        transcribed_by: z.string().min(1),
        transcribed_at: z.iso.date(),
        second_approver: z.string().min(1).nullable(),
        approved_at: z.iso.datetime().nullable(),
      })
      .strict(),
    blocking_issues: z
      .array(
        z
          .object({
            code: identifier,
            severity: z.literal("blocking"),
            description: z.string().min(1),
            source_locators: z.array(z.string().min(1)).min(1),
          })
          .strict(),
      )
      .min(1),
    category_groups: z.array(categoryGroupSchema).length(8),
    categories: z
      .array(
        z
          .object({
            code: identifier,
            group: categoryGroupSchema,
            label: z.string().min(1),
            complexity_g: positiveDecimal,
            source_locator: z.string().min(1),
            z1_crosscheck: z
              .object({
                status: z.enum([
                  "within_range",
                  "conflict_requires_approval",
                  "ambiguous_requires_selection",
                ]),
                candidate_destination: z.string().min(1).nullable(),
                g_min: positiveDecimal.nullable(),
                g_max: positiveDecimal.nullable(),
              })
              .strict(),
          })
          .strict(),
      )
      .min(1),
    services: z
      .array(
        z
          .object({
            code: identifier,
            phase: z.literal("PROGETTAZIONE_PRELIMINARE"),
            description: z.string().min(1),
            source_locator: z.string().min(1),
            q_by_group: qByGroupSchema,
          })
          .strict(),
      )
      .min(1),
    reference_case: z
      .object({
        case_id: identifier,
        components: z
          .array(
            z
              .object({
                component_id: identifier,
                category_code: identifier,
                value_v: positiveDecimal,
                service_codes: z.array(identifier).min(1),
                expected_q_total: positiveDecimal,
                expected_compensation: positiveDecimal,
              })
              .strict(),
          )
          .min(1),
        expected_value_total: positiveDecimal,
        expected_compensation_total: positiveDecimal,
      })
      .strict(),
  })
  .strict()
  .superRefine((ruleset, context) => {
    const unique = (
      values: readonly string[],
      path: (string | number)[],
      label: string,
    ): void => {
      if (new Set(values).size !== values.length) {
        context.addIssue({ code: "custom", message: `Duplicate ${label}`, path });
      }
    };

    unique(ruleset.category_groups, ["category_groups"], "category group");
    unique(ruleset.categories.map((item) => item.code), ["categories"], "category code");
    unique(ruleset.services.map((item) => item.code), ["services"], "service code");
    unique(
      ruleset.reference_case.components.map((item) => item.component_id),
      ["reference_case", "components"],
      "component ID",
    );

    ruleset.reference_case.components.forEach((component, componentIndex) => {
      const category = ruleset.categories.find(
        (item) => item.code === component.category_code,
      );
      if (category === undefined) {
        context.addIssue({
          code: "custom",
          message: `Unknown category: ${component.category_code}`,
          path: ["reference_case", "components", componentIndex, "category_code"],
        });
        return;
      }

      unique(
        component.service_codes,
        ["reference_case", "components", componentIndex, "service_codes"],
        "selected service code",
      );

      let qTotal = new CalcDecimal(0);
      component.service_codes.forEach((serviceCode, serviceIndex) => {
        const service = ruleset.services.find((item) => item.code === serviceCode);
        const qValue = service?.q_by_group[category.group];
        if (service === undefined || qValue === null || qValue === undefined) {
          context.addIssue({
            code: "custom",
            message: `Service ${serviceCode} is unavailable for ${category.group}`,
            path: [
              "reference_case",
              "components",
              componentIndex,
              "service_codes",
              serviceIndex,
            ],
          });
          return;
        }
        qTotal = qTotal.plus(qValue);
      });

      if (decimalString(qTotal) !== decimalString(new CalcDecimal(component.expected_q_total))) {
        context.addIssue({
          code: "custom",
          message: `Q total mismatch for ${component.component_id}`,
          path: ["reference_case", "components", componentIndex, "expected_q_total"],
        });
      }
    });
  });

export type ReferenceRuleset = z.infer<typeof referenceRulesetSchema>;

export function resolveReferenceCaseForVerification(
  input: unknown,
): { ruleset: ReferenceRuleset; calculation: ResolvedCalculationInput } {
  const ruleset = referenceRulesetSchema.parse(input);
  const categories = new Map(ruleset.categories.map((item) => [item.code, item]));
  const services = new Map(ruleset.services.map((item) => [item.code, item]));

  const calculation: ResolvedCalculationInput = {
    rulesetVersion: ruleset.version,
    components: ruleset.reference_case.components.map((component) => {
      const category = categories.get(component.category_code);
      if (category === undefined) throw new Error("Validated category disappeared");

      return {
        componentId: component.component_id,
        label: category.label,
        valueV: component.value_v,
        complexityG: category.complexity_g,
        qItems: component.service_codes.map((serviceCode) => {
          const service = services.get(serviceCode);
          const value = service?.q_by_group[category.group];
          if (service === undefined || value === null || value === undefined) {
            throw new Error("Validated service disappeared");
          }
          return { code: service.code, value };
        }),
      };
    }),
  };

  return { ruleset, calculation };
}
